import axios from 'axios'
import { Message } from 'element-ui'
import router from '../router'
import store from '../store'
import { apiBaseUrl } from './config'
import { getAccessToken, refreshAccessToken, clearTokens } from './auth'

const http = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30_000,
})

function readCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

http.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // ABP 的 AbpAutoValidateAntiforgeryTokenAuthorizationFilter 对所有非 GET 请求强制校验
  // antiforgery token。ABP 内置控制器（/api/identity、/api/permission-management 等）走这套，
  // 缺 token 会被 302 到登录页。ABP 在响应里下发 XSRF-TOKEN cookie，客户端回带
  // RequestVerificationToken 头即可。（自写的 /api/app/* 业务接口不受此过滤，但带上无害。）
  const method = (config.method || 'get').toLowerCase()
  if (method !== 'get' && method !== 'head' && method !== 'options') {
    const xsrf = readCookie('XSRF-TOKEN')
    if (xsrf) {
      config.headers['RequestVerificationToken'] = xsrf
    }
  }
  return config
})

let refreshing = null

// 清账号态并回登录页。必须连 store 一起重置：菜单显隐与路由守卫读的是
// store.state.grantedPolicies，守卫只在它为 null 时才重新拉授权
// （见 router/index.js）。只清 token 不清它，换个账号登录（SPA 不重建）
// 就会沿用上一个账号的策略表——菜单缺项、页面被 fallback 到别处，F5 才恢复。
function forceLogout(message) {
  clearTokens()
  store.dispatch('resetAuth')
  if (router.currentRoute.name !== 'login') {
    router.push({ name: 'login', query: { redirect: router.currentRoute.fullPath } })
  }
  if (message) Message.error(message)
}

http.interceptors.response.use(
  (resp) => {
    // 文件下载（responseType: 'blob'）整条 response 交还调用方——文件名在
    // content-disposition 头里，只给 data 就拿不到了。
    if (resp.config?.responseType === 'blob') {
      // 同样要防 302 到登录页：那种情况回来的是 text/html 的 blob 而非文件。
      if (resp.data && typeof resp.data.type === 'string' && resp.data.type.indexOf('text/html') === 0) {
        forceLogout('登录状态已失效，请重新登录后重试')
        return Promise.reject(new Error('auth redirect to login page'))
      }
      return resp
    }

    // 兜底：ASP.NET 的 StatusCodePages 会把空 body 的 401/403 302 到
    // /Error?httpStatusCode=xxx（未认证还可能 302 到 /Account/Login），浏览器自动跟随后
    // 拿到 HTML 且状态码 200。若不识别，写操作会「假成功」（页面提示已保存但后端没执行）。
    const data = resp.data
    if (typeof data === 'string' && /<!DOCTYPE\s+html|<html[\s>]/i.test(data)) {
      // 403 是「已登录但权限不够」，不是登录失效：只提示，绝不清 token 把人踢去登录页。
      // 否则某个页面缺一项接口权限，表现出来就是「刚登录就被踢」，极难排查。
      if (/httpStatusCode=403/.test(resp.request?.responseURL || '')) {
        Message.error('没有该操作的权限，请联系管理员开通')
        return Promise.reject(Object.assign(new Error('forbidden'), { isForbidden: true }))
      }
      forceLogout('登录状态已失效，请重新登录后重试')
      return Promise.reject(new Error('auth redirect to login page'))
    }
    return data
  },
  async (err) => {
    const status = err.response?.status
    const config = err.config
    if (status === 401 && !config._retried) {
      config._retried = true
      try {
        if (!refreshing) refreshing = refreshAccessToken().finally(() => { refreshing = null })
        await refreshing
        return http(config)
      } catch {
        forceLogout()
        return Promise.reject(err)
      }
    }
    // ABP RemoteServiceErrorResponse: { error: { code, message, details } }
    let body = err.response?.data
    // 下载请求（responseType: 'blob'）出错时 body 也是 Blob，直接读 error.message 只会拿到 undefined，
    // 于是「档案里没有该患者」这类后端提示会退化成 "Request failed with status code 403"。先把它读回 JSON。
    if (body instanceof Blob) {
      try {
        body = JSON.parse(await body.text())
      } catch {
        body = null
      }
    }
    const friendly = body?.error?.message
      || (status === 403 ? '没有该操作的权限，请联系管理员开通' : null)
      || err.message || '请求失败'
    if (status === 403) err.isForbidden = true
    // skipErrorToast：调用方自己消化错误并渲染成页面状态（如「我的患者」的
    // 非专家引导空态）时置 true，避免探测型请求把 403 弹成全局错误。
    if (!config?.skipErrorToast) Message.error(friendly)
    return Promise.reject(err)
  },
)

export default http
